import type { UpdateOrganisationMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateOrganisationDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

export interface UpdateOrganisationScimWrapperInput {
  OrgKey: string;
  ScimEnabled: boolean;
}

/**
 * `updateOrganisation` (ScimEnabled toggle) mutation. Accepts
 * `{ OrgKey, ScimEnabled }` to match the existing call site. The tRPC path
 * drops `OrgKey` because the server reads it from `ctx.orgKey`.
 */
export const useUpdateOrganisationScim = createMutationHook({
  name: 'updateOrganisationScim',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.organisation.updateScim.mutationOptions({ onSuccess }),
  mapInput: (input: UpdateOrganisationScimWrapperInput) => ({
    ScimEnabled: input.ScimEnabled,
  }),
  // Invalidate any organisation-related tRPC queries. The legacy GraphQL
  // `GetOrganisationDocument` is refetched manually via Apollo `refetch()`
  // in the consumer; this covers any future tRPC organisation queries.
  invalidate: (trpc) => [trpc.frontend.organisation.pathKey()],
  // Maps the tRPC mutation response to match the GraphQL mutation response
  // shape so consumers branching on the result do not need to know which
  // path executed.
  mapResponse: (result: {
    affected_rows: number;
  }): UpdateOrganisationMutation => ({
    update_auth_organisation: {
      __typename: 'auth_organisation_mutation_response',
      affected_rows: result.affected_rows,
    },
  }),
  graphqlDocument: UpdateOrganisationDocument,
  graphqlVariables: (input: UpdateOrganisationScimWrapperInput) => ({
    OrgKey: input.OrgKey,
    ScimEnabled: input.ScimEnabled,
  }),
});
